import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Create the configuration context
const ConfigContext = createContext();

// Default configuration values as fallbacks
const DEFAULT_CONFIG = {
  app: {
    name: 'GeoDataBot',
    version: '0.8.5-dev1',
    shortDescription: 'AI-powered geoscience data analysis and exploration assistant',
    longDescription: 'An advanced AI assistant specialized in geoscience data analysis, geological interpretation, and earth science research.',
    companyDescription: 'Intelligent Data Design Inc.',
    userInstructions: '',
    favicon: ''
  },
  googleClientId: '',
  showPersonality: false,
  showCategory: false,
  showPhotoControl: false,
  categories: {
    default: {
      name: 'Default',
      personalities: []
    }
  },
  personalities: [] // Backward compatibility
};

/**
 * Configuration Provider Component
 * Fetches app configuration from the backend and provides it to all child components
 */
export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(0);

  // Fetch configuration from backend with caching
  const fetchConfig = useCallback(async (forceRefresh = false) => {
    // Cache for 5 minutes to prevent excessive calls
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    
    if (!forceRefresh && (now - lastFetch) < CACHE_DURATION && config !== DEFAULT_CONFIG) {
      console.log('[CONFIG] Using cached configuration');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/config', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch configuration: ${response.status} ${response.statusText}`);
      }

      const configData = await response.json();
      
      // Merge with defaults to ensure all required fields exist
      const mergedConfig = {
        ...DEFAULT_CONFIG,
        ...configData,
        app: {
          ...DEFAULT_CONFIG.app,
          ...configData.app
        },
        categories: configData.categories || DEFAULT_CONFIG.categories,
        personalities: configData.personalities || DEFAULT_CONFIG.personalities,
        showPersonality: configData.showPersonality !== undefined ? configData.showPersonality : DEFAULT_CONFIG.showPersonality,
        showCategory: configData.showCategory !== undefined ? configData.showCategory : DEFAULT_CONFIG.showCategory,
        showPhotoControl: configData.showPhotoControl !== undefined ? configData.showPhotoControl : DEFAULT_CONFIG.showPhotoControl
      };

      setConfig(mergedConfig);
      setLastFetch(now);
      console.log('[CONFIG] Configuration loaded successfully:', mergedConfig);
      
    } catch (err) {
      console.error('[CONFIG] Failed to load configuration:', err);
      setError(err.message);
      
      // Use default configuration on error
      setConfig(DEFAULT_CONFIG);
      console.log('[CONFIG] Using default configuration due to error');
      
    } finally {
      setLoading(false);
    }
  }, [lastFetch, config]);

  // Load configuration on component mount
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Retry function for manual retry attempts
  const retry = () => {
    fetchConfig(true);
  };

  // Helper function to get personalities from default category (backward compatibility)
  const getPersonalities = () => {
    if (config.categories && config.categories.default) {
      return config.categories.default.personalities || [];
    }
    return config.personalities || [];
  };

  // Context value
  const value = {
    config,
    loading,
    error,
    retry,
    // Convenience getters for common config values
    appName: config.app.name,
    appVersion: config.app.version,
    appShortDescription: config.app.shortDescription,
    appLongDescription: config.app.longDescription,
    appCompanyDescription: config.app.companyDescription,
    appUserInstructions: config.app.userInstructions,
    appFavicon: config.app.favicon,
    googleClientId: config.googleClientId,
    categories: config.categories,
    personalities: getPersonalities(), // Use helper for backward compatibility
    showPersonality: config.showPersonality,
    showCategory: config.showCategory,
    showPhotoControl: config.showPhotoControl,
    // Helper methods
    getPersonalities,
    getCategoryPersonalities: (categoryName = 'default') => {
      if (config.categories && config.categories[categoryName]) {
        return config.categories[categoryName].personalities || [];
      }
      return [];
    },
    getShowPersonality: () => config.showPersonality,
    getShowCategory: () => config.showCategory
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

/**
 * Hook to access configuration context
 * Throws an error if used outside of ConfigProvider
 */
export const useConfigContext = () => {
  const context = useContext(ConfigContext);
  
  if (context === undefined) {
    throw new Error('useConfigContext must be used within a ConfigProvider');
  }
  
  return context;
};

// Export the context for testing purposes
export { ConfigContext };
